// ─── Setup ───────────────────────────────────────────────────────────────────
//
// "First setup area stays simple and tile-based (`Add service` style action
// tiles, progressive disclosure, not a dense settings screen)."
//
// So: three tiles, one open at a time. A closed tile shows what is currently
// configured, which is what somebody arriving actually wants to know — a
// settings screen that shows only labels makes you open everything to find out
// where you are.
//
// ── Saying what a change will really do ─────────────────────────────────────
//
// Two of these edits look harmless and are not:
//
//   • Changing a service's duration does NOT move appointments already booked.
//     The booking record stores its own end time precisely so a later edit
//     cannot silently reschedule somebody. The screen says so, because the
//     natural assumption is the opposite.
//
//   • Changing working hours does NOT cancel bookings already outside them.
//     Those stay, and the calendar shows them against closed time. Removing
//     them would be the setup screen quietly cancelling appointments.
//
// Both are honest consequences of decisions made in the module. Neither is
// discoverable from the form itself, which is exactly why they are written on
// it.

import { useEffect, useState } from "react";
import {
  WEEKDAYS, validateService, validateHours, parsePrice, formatPrice,
  BOOKING_MODE_COPY, BOOKING_MODES,
  type DayHours, type BookingMode,
} from "./setup.js";

export type SetupService = {
  id: string;
  name: string;
  durationMinutes: number;
  description?: string | null;
  priceMinor?: number | null;
  currency?: string | null;
};

export type SetupAreaProps = {
  services: SetupService[];
  hours: DayHours[];
  mode: BookingMode;
  onAddService: (draft: { name: string; durationMinutes: number; description?: string; priceMinor?: number | null }) => void;
  /**
   * Change one in place.
   *
   * Editing rather than remove-and-re-add, which was the only way to fix a
   * typo before 2026-09-15 and is the wrong shape: a new row means every
   * booking already made points at a retired service, and the diary starts
   * saying "no service" for work that was actually done.
   */
  onEditService: (serviceId: string, patch: {
    name: string; durationMinutes: number; priceMinor: number | null;
  }) => void;
  onRemoveService: (serviceId: string) => void;
  /**
   * Make a copy of one, to change rather than build from scratch (Drea,
   * 2026-10-04). Optional: the Duplicate button shows only for an app that
   * passes this, so a product that has not asked for it does not grow one.
   */
  onDuplicateService?: (service: SetupService) => void;
  /**
   * Summary tiles and hire-type cards as INFO CARDS, mid green (Drea's
   * standing rule, 2026-10-05; --nl-info-card). Opt-in: this component sets
   * its own theme, so a product's own colours never reach inside it, and
   * one that has not asked must not change colour by accident.
   */
  infoCards?: boolean;
  /**
   * Services are RATES charged by the half hour (Memorial Hall's hire rates,
   * Drea 2026-10-05: Business £7.50, Domestic £7.00, Charity £6.00). Each
   * shows "£7.50 per half hour", and a rate has no length of its own to set.
   * Opt-in: Crafting Comb's services are appointments with lengths.
   */
  perHalfHour?: boolean;
  onChangeHours: (hours: DayHours[]) => void;
  /**
   * Absent for a product with no customer booking page — Memorial Hall's
   * bookings are all taken by the Secretary — where "how customers book"
   * would be a setting about nobody. The tile is then not drawn.
   */
  onChangeMode?: (mode: BookingMode) => void;
  /** The product's own words for the two tiles — content, not layout. A hall
   *  hires out "Hire types" over "Opening hours"; a salon sells "Services"
   *  in "Working hours". */
  labels?: { services?: string; serviceNoun?: [singular: string, plural: string]; hours?: string };
};

type OpenTile = "services" | "hours" | "mode" | null;

/** A summary tile as an info card: mid green, light ink. */
const infoTile: React.CSSProperties = {
  background: "var(--nl-info-card)",
  color: "var(--nl-info-card-ink)",
  border: "1px solid var(--nl-info-card-stroke)",
};

const tile: React.CSSProperties = {
  background: "var(--nl-surface)",
  border: "1px solid var(--nl-stroke)",
  borderRadius: "var(--nl-radius-card)",
  padding: 14,
  textAlign: "left",
  font: "inherit",
  color: "var(--nl-ink)",
  cursor: "pointer",
  width: "100%",
  display: "flex", flexDirection: "column", gap: 4,
};

const field: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)",
  borderRadius: "var(--nl-radius-control)",
  border: "1px solid var(--nl-stroke)",
  background: "var(--nl-surface-raised)",
  color: "var(--nl-ink)", padding: "0 12px", font: "inherit", width: "100%",
};

const action: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)",
  borderRadius: "var(--nl-radius-control)",
  border: "1px solid var(--nl-stroke)",
  background: "var(--nl-state-open)",
  color: "var(--nl-state-open-ink)",
  padding: "0 16px", font: "inherit", cursor: "pointer",
};

const note: React.CSSProperties = {
  margin: "8px 0 0", fontSize: "0.78rem", color: "var(--nl-ink-faint)", lineHeight: 1.45,
};

export function SetupArea({
  services, hours, mode, onAddService, onEditService, onRemoveService, onDuplicateService, infoCards = false, perHalfHour = false,
  onChangeHours, onChangeMode, labels,
}: SetupAreaProps) {
  const [open, setOpen] = useState<OpenTile>(null);
  const [one, many] = labels?.serviceNoun ?? ["service", "services"];

  const summary = {
    services: services.length === 0
      ? "None yet"
      : `${services.length} ${services.length === 1 ? one : many}`,
    hours: (() => {
      const openDays = hours.filter(h => h.open);
      if (openDays.length === 0) return "Not set — you will not appear bookable";
      return `${openDays.length} day${openDays.length === 1 ? "" : "s"} a week`;
    })(),
    mode: BOOKING_MODE_COPY[mode].title,
  };

  const Tile = ({ id, title, value }: { id: Exclude<OpenTile, null>; title: string; value: string }) => (
    <button
      type="button"
      data-testid={`tile-${id}`}
      aria-expanded={open === id}
      onClick={() => setOpen(open === id ? null : id)}
      style={{ ...tile, ...(infoCards ? infoTile : { background: open === id ? "var(--nl-surface-raised)" : "var(--nl-surface)" }) }}
    >
      <span style={{ fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: infoCards ? "var(--nl-info-card-ink-dim)" : "var(--nl-ink-faint)" }}>
        {title}
      </span>
      {/* The current setting on the closed tile: arriving at a settings screen
          and being shown only labels means opening everything to find out
          where you are. */}
      <span style={{ fontSize: "1rem" }}>{value}</span>
    </button>
  );

  return (
    <div data-nl-theme="nulife" data-testid="setup-area" style={{
      display: "flex", flexDirection: "column", gap: "var(--nl-tile-gap)", maxWidth: 560, fontFamily: "var(--nl-font)",
      // Hire-type cards follow the same rule as the summary tiles.
      ...(infoCards ? {
        "--nl-setup-card": "var(--nl-info-card)",
        "--nl-setup-card-ink": "var(--nl-info-card-ink)",
      } as React.CSSProperties : {}),
    }}>
      <Tile id="services" title={labels?.services ?? "Services"} value={summary.services} />
      {open === "services" && (
        <ServicesPanel
          services={services}
          onAdd={onAddService}
          onEdit={onEditService}
          onRemove={onRemoveService}
          onDuplicate={onDuplicateService}
          perHalfHour={perHalfHour}
          noun={labels?.serviceNoun?.[0]}
        />
      )}

      <Tile id="hours" title={labels?.hours ?? "Working hours"} value={summary.hours} />
      {open === "hours" && <HoursPanel hours={hours} onChange={onChangeHours} />}

      {onChangeMode && (
        <>
          <Tile id="mode" title="Booking mode" value={summary.mode} />
          {open === "mode" && <ModePanel mode={mode} onChange={onChangeMode} />}
        </>
      )}

      <p style={note}>
        {/* Says where the missing thing lives, rather than leaving somebody
            hunting through settings for it. */}
        Blocking a lunch break, a holiday or a one-off afternoon happens on the
        calendar, not here.
      </p>
    </div>
  );
}

/**
 * One service, open for editing.
 *
 * Its own component so the draft state belongs to the row being edited and
 * dies with it. Held in the panel instead, a draft would survive opening a
 * DIFFERENT service and pre-fill it with the last one's values — which is how
 * somebody saves a price onto the wrong service.
 *
 * The same `validateService` as the add form, so "what makes a service valid"
 * has one answer rather than two that drift.
 */
function ServiceEditor({ service, onSave, onCancel, perHalfHour = false }: {
  perHalfHour?: boolean;
  service: SetupService;
  onSave: (patch: { name: string; durationMinutes: number; priceMinor: number | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(service.name);
  const [duration, setDuration] = useState(String(service.durationMinutes));
  // Shown in pounds, stored in pence. Seeded from what is there, blank when
  // there is no price — a service quoted on consultation is a real thing.
  const [price, setPrice] = useState(
    service.priceMinor == null ? "" : (service.priceMinor / 100).toFixed(2),
  );

  const durationMinutes = Number(duration) || null;
  const problems = validateService({ name, durationMinutes, priceText: price });
  const ready = problems.length === 0;

  return (
    <div
      data-testid="service-editor"
      style={{
        display: "flex", flexDirection: "column", gap: 8,
        borderRadius: "var(--nl-radius-tile)",
        border: "1px solid var(--nl-state-open)",
        background: "var(--nl-surface)", padding: "10px 12px",
      }}
    >
      <label style={labelStyle}>
        Name
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          style={field}
          data-testid="edit-service-name"
          autoFocus
        />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        {!perHalfHour && (
        <label style={{ ...labelStyle, flex: 1 }}>
          Minutes
          <input
            value={duration}
            onChange={e => setDuration(e.target.value)}
            inputMode="numeric"
            style={field}
            data-testid="edit-service-duration"
          />
        </label>
        )}
        <label style={{ ...labelStyle, flex: 1 }}>
          {perHalfHour ? "Price per half hour" : "Price (blank for none)"}
          <input
            value={price}
            onChange={e => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="35.00"
            style={field}
            data-testid="edit-service-price"
          />
        </label>
      </div>
      {problems.length > 0 && (
        <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--nl-status-declined)" }}>
          {problemText(problems)}
        </p>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="save-service"
          disabled={!ready}
          onClick={() => onSave({
            name: name.trim(),
            durationMinutes: durationMinutes!,
            // `parsePrice` already answers null for an empty string, so the
            // blank case needs no branch of its own — one that looked like it
            // was doing work survived a mutation because it was not.
            priceMinor: parsePrice(price),
          })}
          style={{ ...action, minHeight: 36, padding: "0 14px", opacity: ready ? 1 : 0.5 }}
        >Save</button>
        <button
          type="button"
          data-testid="cancel-service-edit"
          onClick={onCancel}
          style={{ ...action, background: "transparent", color: "var(--nl-ink-dim)", minHeight: 36, padding: "0 12px" }}
        >Cancel</button>
      </div>
    </div>
  );
}

/**
 * What is wrong with a draft service, in words.
 *
 * Shared by the add form and the editor. Two copies of this wording would
 * drift, and the second copy is where "up to eight hours" would quietly stop
 * matching what `validateService` actually allows.
 */
function problemText(problems: ReturnType<typeof validateService>): string {
  return [
    problems.includes("name") ? "A service needs a name." : "",
    problems.includes("duration") ? "Give it a length in minutes, up to eight hours." : "",
    problems.includes("price") ? "That price could not be read — try 35 or 35.50." : "",
  ].filter(Boolean).join(" ");
}

const labelStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4,
  fontSize: "0.8rem", color: "var(--nl-ink-dim)",
};

function ServicesPanel({ services, onAdd, onEdit, onRemove, onDuplicate, perHalfHour = false, noun }: {
  perHalfHour?: boolean;
  noun?: string;
  services: SetupService[];
  onAdd: SetupAreaProps["onAddService"];
  onEdit: SetupAreaProps["onEditService"];
  onRemove: (id: string) => void;
  onDuplicate?: SetupAreaProps["onDuplicateService"];
}) {
  const [name, setName] = useState("");
  // A rate is charged by the half hour, so that is its length.
  const [duration, setDuration] = useState(perHalfHour ? "30" : "60");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);
  /** Which service is open for editing. One at a time: two half-finished
   *  edits on one screen is a way to save the wrong one. */
  const [editing, setEditing] = useState<string | null>(null);

  const durationMinutes = Number(duration) || null;
  const problems = validateService({ name, durationMinutes, priceText: price });
  const ready = problems.length === 0;

  return (
    <section data-testid="panel-services" style={{ ...tile, cursor: "default", gap: 12 }}>
      {services.length === 0 && (
        <p style={{ margin: 0, color: "var(--nl-ink-dim)", fontSize: "0.9rem" }}>
          No services yet. Customers cannot book until there is at least one.
        </p>
      )}

      {services.map(s => (
        editing === s.id ? (
          <ServiceEditor
            perHalfHour={perHalfHour}
            key={s.id}
            service={s}
            onCancel={() => setEditing(null)}
            onSave={patch => { onEdit(s.id, patch); setEditing(null); }}
          />
        ) : (
        <div
          key={s.id}
          data-testid="service-row"
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
            // Buttons drop below the name on a phone rather than squeezing it
            // into a column a word wide.
            flexWrap: "wrap",
            borderRadius: "var(--nl-radius-tile)", border: "1px solid var(--nl-stroke)",
            // Its own token rather than --nl-surface, which is the panel's
            // colour too: a card the same colour as what it sits on cannot be
            // seen (Drea, 2026-10-04). Mid-green in Memorial Hall's theme;
            // each other theme keeps its own look (theme.css).
            background: "var(--nl-setup-card)", color: "var(--nl-setup-card-ink)",
            padding: "10px 12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 160px", minWidth: 0 }}>
            <div>{s.name}</div>
            {/* Time and amount, larger, and the time brighter still (Drea,
                2026-10-04): they were small dim text, easy to miss. */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
              {perHalfHour ? (
                <span data-testid="service-price" style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--nl-setup-card-ink)" }}>
                  {formatPrice(s.priceMinor, s.currency) ? `${formatPrice(s.priceMinor, s.currency)} per half hour` : "No price yet"}
                </span>
              ) : (
              <>
              <span data-testid="service-duration" style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--nl-setup-card-ink)" }}>
                {s.durationMinutes} minutes
              </span>
              {formatPrice(s.priceMinor, s.currency) && (
                <span data-testid="service-price" style={{ fontSize: "1.05rem", fontWeight: 600 }}>
                  {formatPrice(s.priceMinor, s.currency)}
                </span>
              )}
              </>
              )}
            </div>
          </div>

          {confirming === s.id ? (
            // Two steps, because removing a service is not undoable from here
            // and a mis-tap on a phone is easy.
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: "0.78rem", color: "var(--nl-setup-card-ink)" }}>Remove?</span>
              <button
                type="button" data-testid="confirm-remove"
                onClick={() => { onRemove(s.id); setConfirming(null); }}
                style={{ ...action, background: "var(--nl-status-declined)", minHeight: 36, padding: "0 12px" }}
              >Yes</button>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                style={{ ...action, background: "transparent", color: "var(--nl-setup-card-ink)", minHeight: 36, padding: "0 12px" }}
              >No</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button
                type="button" data-testid="edit-service"
                onClick={() => setEditing(s.id)}
                style={{ ...action, background: "transparent", color: "var(--nl-setup-card-ink)", minHeight: 36, padding: "0 12px" }}
              >Edit</button>
              {onDuplicate && (
                <button
                  type="button" data-testid="duplicate-service"
                  onClick={() => onDuplicate(s)}
                  style={{ ...action, background: "transparent", color: "var(--nl-setup-card-ink)", minHeight: 36, padding: "0 12px" }}
                >Duplicate</button>
              )}
              <button
                type="button" data-testid="remove-service"
                onClick={() => setConfirming(s.id)}
                style={{ ...action, background: "transparent", color: "var(--nl-setup-card-ink)", opacity: 0.85, minHeight: 36, padding: "0 12px" }}
              >Remove</button>
            </div>
          )}
        </div>
        )
      ))}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Name
          <input value={name} onChange={e => setName(e.target.value)} style={field} />
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          {!perHalfHour && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)", flex: 1 }}>
            Minutes
            <input value={duration} onChange={e => setDuration(e.target.value)} inputMode="numeric" style={field} />
          </label>
          )}
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)", flex: 1 }}>
            {perHalfHour ? "Price per half hour" : "Price (optional)"}
            <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" placeholder="35.00" style={field} />
          </label>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Description (optional)
          <input value={description} onChange={e => setDescription(e.target.value)} style={field} />
        </label>

        <button
          type="button"
          data-testid="add-service"
          disabled={!ready}
          onClick={() => {
            onAdd({
              name: name.trim(),
              durationMinutes: durationMinutes!,
              description: description.trim() || undefined,
              priceMinor: price.trim() ? parsePrice(price) : null,
            });
            setName(""); setDuration(perHalfHour ? "30" : "60"); setPrice(""); setDescription("");
          }}
          style={{ ...action, opacity: ready ? 1 : 0.5, cursor: ready ? "pointer" : "not-allowed" }}
        >Add {noun ?? "service"}</button>

        {/* Names what is wrong, rather than leaving a disabled button as a
            dead end. */}
        {!ready && (name || price) && (
          <p data-testid="service-problem" style={{ ...note, color: "var(--nl-status-requested)" }}>
            {problemText(problems)}
          </p>
        )}
      </div>

      <p style={note}>
        Changing how long a service takes affects new bookings only. Appointments
        already in the diary keep the length they were booked at.
      </p>
    </section>
  );
}

function HoursPanel({ hours, onChange }: { hours: DayHours[]; onChange: (h: DayHours[]) => void }) {
  // ── Typed into a draft, saved when it is a real time ──────────────────────
  //
  // These boxes used to save on every keystroke. A half-typed time cannot be
  // read, it was saved as 00:00, the screen reloaded, and the box snapped
  // back: "stuck at 00:00 and can't be changed" (Drea, 2026-10-04). Now what
  // is typed stays here until the box is left, and is only saved when every
  // day reads as a real time. A problem is shown, never saved.
  const [draft, setDraft] = useState<DayHours[]>(hours);
  const [dirty, setDirty] = useState(false);
  const saved = JSON.stringify(hours);
  useEffect(() => { if (!dirty) setDraft(hours); }, [saved]); // eslint-disable-line react-hooks/exhaustive-deps

  const problems = validateHours(draft);
  const problemFor = (weekday: number) => problems.find(p => p.weekday === weekday);

  const commit = (next: DayHours[]) => {
    if (validateHours(next).length > 0) return; // shown, not saved
    setDirty(false);
    if (JSON.stringify(next) !== saved) onChange(next);
  };
  const edit = (weekday: number, patch: Partial<DayHours>) => {
    setDirty(true);
    setDraft(d => d.map(h => (h.weekday === weekday ? { ...h, ...patch } : h)));
  };
  /** A tick is a whole decision: saved at once (if the rest is valid). */
  const update = (weekday: number, patch: Partial<DayHours>) => {
    const next = draft.map(h => (h.weekday === weekday ? { ...h, ...patch } : h));
    setDraft(next);
    commit(next);
  };

  return (
    <section data-testid="panel-hours" style={{ ...tile, cursor: "default", gap: 8 }}>
      {draft.map(day => {
        const problem = problemFor(day.weekday);
        return (
          <div key={day.weekday} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 130, minHeight: "var(--nl-hit-min)" }}>
              <input
                type="checkbox"
                checked={day.open}
                onChange={e => update(day.weekday, { open: e.target.checked })}
                aria-label={WEEKDAYS[day.weekday]}
              />
              <span style={{ color: day.open ? "var(--nl-ink)" : "var(--nl-ink-faint)" }}>
                {WEEKDAYS[day.weekday]}
              </span>
            </label>

            {day.open && (
              <>
                {/* A real time input, as on the calendar's AddEntry: the hour
                    and minutes highlight when tapped and a phone shows its own
                    time picker. These were plain text boxes, where a click
                    gave no feedback and pressing 8 typed "08:080" (Drea,
                    2026-10-04). Still saved only when the box is left. */}
                <input
                  type="time"
                  step={300}
                  value={day.from}
                  onChange={e => edit(day.weekday, { from: e.target.value })}
                  onBlur={() => commit(draft)}
                  aria-label={`${WEEKDAYS[day.weekday]} from`}
                  style={{ ...field, width: 96 }}
                />
                <span style={{ color: "var(--nl-ink-faint)" }}>to</span>
                <input
                  type="time"
                  step={300}
                  value={day.to}
                  onChange={e => edit(day.weekday, { to: e.target.value })}
                  onBlur={() => commit(draft)}
                  aria-label={`${WEEKDAYS[day.weekday]} to`}
                  style={{ ...field, width: 96 }}
                />
              </>
            )}

            {problem && (
              // A day that ends before it starts produces no availability at
              // all, silently — the business would simply never appear
              // bookable on it.
              <span data-testid="hours-problem" style={{ fontSize: "0.78rem", color: "var(--nl-status-requested)" }}>
                {problem.reason === "backwards"
                  ? "Ends before it starts, so nothing can be booked."
                  : "Give both times, or untick the day."}
              </span>
            )}
          </div>
        );
      })}

      <p style={note}>
        Changing your hours does not cancel appointments already booked outside
        them. Those stay in the diary, and the calendar shows them against
        closed time.
      </p>
    </section>
  );
}

function ModePanel({ mode, onChange }: { mode: BookingMode; onChange: (m: BookingMode) => void }) {
  return (
    <section data-testid="panel-mode" style={{ ...tile, cursor: "default", gap: 8 }} role="radiogroup" aria-label="Booking mode">
      {BOOKING_MODES.map(m => {
        const selected = m === mode;
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`mode-${m}`}
            onClick={() => onChange(m)}
            style={{
              ...tile,
              background: selected ? "var(--nl-state-open)" : "var(--nl-surface)",
              color: selected ? "var(--nl-state-open-ink)" : "var(--nl-ink)",
              border: `1px solid ${selected ? "var(--nl-state-open)" : "var(--nl-stroke)"}`,
            }}
          >
            <span style={{ fontSize: "1rem" }}>{BOOKING_MODE_COPY[m].title}</span>
            <span style={{ fontSize: "0.82rem", opacity: 0.9 }}>{BOOKING_MODE_COPY[m].detail}</span>
          </button>
        );
      })}
    </section>
  );
}
